/**
 * Navigation Guards (Shared)
 *
 * Handles:
 * - Authentication checks for protected routes
 * - Role-based access control
 * - Redirect logic for unauthenticated users
 */
import type { RouteLocationNormalized, Router } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { isChunkLoadError, attemptChunkReload, clearedChunkReloadQuery } from './chunk-reload'
import {
  DEFAULT_GRANTED_BY,
  clearRouteDenial,
  recordRouteDenial,
  type RouteDenial,
} from './denial-state'

// Route meta type augmentation
declare module 'vue-router' {
  interface RouteMeta {
    /** Whether the route requires authentication */
    requiresAuth?: boolean
    /**
     * Required roles. The guard below does an EXACT `includes(userRole)` on
     * this list — there is no admin-implies-everything fallback — so a route
     * that means "admin or legal" must say BOTH. `['legal']` alone locks every
     * admin out of the route it names.
     */
    roles?: Array<'admin' | 'staff' | 'client' | 'developer' | 'legal'>
    /** Layout to use (for layout switching) */
    layout?: 'default' | 'auth' | 'minimal' | 'fullscreen'
    /** Page title for document.title */
    title?: string
    /**
     * Who can grant this route to someone the `roles` list turns away, in the
     * denied person's words ("Ahmed", "your consultant", "a ClaimShield
     * administrator"). Read by the role-denial branch below and handed to the
     * app through `routeDenial`; a route that says nothing falls back to
     * `DEFAULT_GRANTED_BY`, so this is additive and no route has to set it.
     *
     * It lives on the route rather than in the guard because only the route
     * knows: the Atlas surfaces are Ahmed's to open, the legal ones are not,
     * and a guard that guessed would send people to the wrong person.
     */
    grantedBy?: string
    /**
     * Full-bleed page container — drops the 1800px `.page-container` cap for
     * wide-canvas pages (kanban boards, timelines) that need the whole screen
     * on an ultrawide display. Honored by OpsLayout.
     */
    wide?: boolean
  }
}

/**
 * Would `routeName` actually admit `role`, or would landing there bounce?
 *
 * `router.hasRoute` answers "does this route exist", which is NOT the question.
 * A route that exists and then denies the arriving user is precisely how the
 * redirect loop below is built. `resolve()` merges meta across every matched
 * record (child wins per key), so this reads the same `meta.roles` the guard
 * itself will read on arrival — the ops AI Review Queue overrides its parent's
 * ['staff','admin'] with ['admin','developer'], and that override is only
 * visible after a resolve.
 *
 * A route declaring no roles admits everyone, matching the guard's own rule.
 * `resolve` throws for a named route needing params, so a home candidate that
 * cannot be built without them counts as no home rather than as a crash.
 */
function routeAdmitsRole(router: Router, routeName: string, role?: string): boolean {
  if (!router.hasRoute(routeName)) return false
  try {
    const roles = router.resolve({ name: routeName }).meta?.roles
    if (!roles || roles.length === 0) return true
    return !!role && (roles as readonly string[]).includes(role)
  } catch {
    return false
  }
}

/**
 * Where a denied or already-authenticated user should land — or `null` when
 * this app has no landing place that admits them at all.
 *
 * WHY `null` IS A REAL ANSWER AND NOT A DEFENSIVE FLOURISH. This function used
 * to promise a route unconditionally, falling back to `dashboard`. In the ops
 * app that is fine: a denied developer lands on the AI Review Queue, which
 * admits them. In the CLIENT PORTAL it was a live freeze — the portal has no
 * `file-review-queue`, so a denied developer was sent to `dashboard`, the very
 * route that had just denied them, and the guard bounced until the tab died
 * (2026-09-03, first developer-role login on portal.claimshieldpro.com).
 *
 * The old comment named the portal as the SAFE case ("there it falls back to
 * dashboard as before"). The portal was the only unsafe one, because "as
 * before" had never been exercised by a role the layout route excludes. So the
 * candidate list is now CHECKED rather than assumed, which also closes the
 * same latent loop for a client-role account that opens the ops app.
 */
function homeRouteFor(router: Router, role?: string): { name: string } | null {
  // `legal` is the second role (after `developer`) whose home is NOT the
  // dashboard, and leaving it out is a live logout bug rather than a missing
  // convenience: `legal` has is_staff=false and is_admin=false, so it is
  // denied by `dashboard` (roles ['staff','admin']), the candidate list below
  // denies it again, this function returns null, and the guard's last branch
  // calls authStore.logout(). The account types the right password and is
  // thrown straight back to /login?denied=app. The ops app registers
  // `legal-waiting`; an app that does not (the portal) still resolves to null
  // here, because routeAdmitsRole checks rather than assumes.
  const candidates =
    role === 'developer'
      ? ['file-review-queue', 'dashboard']
      : role === 'legal'
        ? ['legal-waiting']
        : ['dashboard']
  for (const name of candidates) {
    if (routeAdmitsRole(router, name, role)) return { name }
  }
  return null
}

/**
 * The denial carrier lives in `./denial-state.ts`, not here.
 *
 * It moved there for AST3-1 (2026-09-22): the carrier has to be cleared by the
 * auth store's `clearAuth()` — the one teardown behind both `logout()` and a
 * failed session restore — and this module imports `../stores/auth`, so the
 * store cannot import it back without a cycle. `denial-state.ts` imports `vue`
 * and nothing else, so both sides hold it. Every reason the carrier is a module
 * ref rather than a view or a query string, and its full session lifetime, are
 * documented there.
 *
 * Re-exported here because that is where five apps and the specs already import
 * it from; `recordRouteDenial` is deliberately NOT re-exported, so the guard
 * below stays the only writer through this surface.
 */
export type { RouteDenial } from './denial-state'
export {
  DEFAULT_GRANTED_BY,
  routeDenial,
  peekRouteDenial,
  consumeRouteDenial,
  clearRouteDenial,
} from './denial-state'

/**
 * "staff" · "staff and admin" · "staff, admin and developer".
 *
 * Joined for a sentence rather than printed as an array: `Required roles:
 * staff,admin` is the warn line's voice, not a person's.
 */
function formatRoleList(roles: readonly string[]): string {
  if (roles.length <= 1) return roles[0] ?? ''
  return `${roles.slice(0, -1).join(', ')} and ${roles[roles.length - 1]}`
}

/**
 * The denial a bounce would otherwise have thrown away.
 *
 * `to.fullPath` rather than `to.path` is the whole point of M9 A: the query is
 * where `?tab=`, `?as_of=` and `?page=` live, so a denial that keeps the path
 * and loses the query has kept the useless half.
 */
function buildRouteDenial(to: RouteLocationNormalized, userRole?: string): RouteDenial {
  const requiredRoles = (to.meta.roles ?? []) as readonly string[]
  const allowed = formatRoleList(requiredRoles)
  return {
    reason: userRole
      ? `This screen is open to ${allowed}. You are signed in as ${userRole}.`
      : `This screen is open to ${allowed}. Your account has no role that can open it.`,
    grantedBy: to.meta.grantedBy ?? DEFAULT_GRANTED_BY,
    originalUrl: to.fullPath,
    deniedPath: to.path,
    requiredRoles,
    userRole,
    at: Date.now(),
  }
}

/**
 * Setup all navigation guards on the router
 */
export function setupGuards(router: Router): void {
  router.beforeEach(async (to) => {
    // DEBUG: Log IMMEDIATELY before any logic (dev only — keeps route params,
    // names, and matched paths out of the production console)
    if (import.meta.env.DEV) {
      console.log('[Guard] ===== NAVIGATION START =====')
      console.log('[Guard] Target:', {
        path: to.path,
        fullPath: to.fullPath,
        name: to.name,
        params: to.params,
        matched: to.matched.map(r => ({ path: r.path, name: r.name }))
      })
    }

    const authStore = useAuthStore()

    // Set page title
    const title = to.meta.title || 'ClaimShield'
    document.title = title

    // DEBUG: Log route meta details
    if (import.meta.env.DEV) {
      console.log('[Guard] Route meta:', {
        requiresAuth: to.meta.requiresAuth,
        layout: to.meta.layout,
        roles: to.meta.roles
      })
    }

    // Public routes - no auth required
    if (to.meta.requiresAuth === false) {
      if (import.meta.env.DEV) console.log('[Guard] ✅ Public route detected, allowing access')
      // If already logged in and going to login, redirect to their home — but
      // only to a home that will actually admit them. This is the SECOND loop
      // shape and it closes independently of the first: bouncing an
      // authenticated visitor from /login to a route that denies them yields
      // login → dashboard → login, forever. No home means they stay here.
      if (to.name === 'login' && authStore.isAuthenticated) {
        const home = homeRouteFor(router, authStore.user?.role)
        if (home) return home
      }
      return true
    }

    if (import.meta.env.DEV) console.log('[Guard] ⚠️ Route requires auth (requiresAuth not explicitly false)')

    // Routes without explicit requiresAuth=false require authentication
    // Check if authenticated
    if (!authStore.isAuthenticated) {
        if (import.meta.env.DEV) console.log('[Guard] User not authenticated, checking initialization...')
        // Try to restore session first
        if (!authStore.initialized) {
          if (import.meta.env.DEV) console.log('[Guard] Initializing auth store...')
          await authStore.initialize()
        }

        // Still not authenticated after init attempt
        if (!authStore.isAuthenticated) {
          if (import.meta.env.DEV) console.log('[Guard] ❌ Still not authenticated, redirecting to login')
          // Save intended destination for redirect after login
          const redirectPath = to.fullPath !== '/login' ? to.fullPath : undefined
          // The restore could not reach the server: the sign-in is kept, and
          // `unreachable` tells the login page to offer a retry, not a form.
          const query: Record<string, string> = {}
          if (redirectPath) query.redirect = redirectPath
          if (authStore.restoreUnreachable) query.unreachable = '1'

          return {
            name: 'login',
            query: Object.keys(query).length ? query : undefined,
          }
        }
      }

    // Check role requirements
    if (to.meta.roles && to.meta.roles.length > 0) {
      const userRole = authStore.user?.role
      if (!userRole || !to.meta.roles.includes(userRole)) {
        // User doesn't have required role — send them to their own home, not a
        // hard dashboard (a developer can't enter the dashboard → bounce loop).
        console.warn(`[Guard] Access denied to ${to.path}. Required roles: ${to.meta.roles}`)
        const home = homeRouteFor(router, userRole)
        if (home) {
          // Record BEFORE the redirect resolves, so the view that mounts on
          // `home` finds the denial already there. The bounce itself is what
          // destroys `to.fullPath`; after this return, nothing can reconstruct it.
          recordRouteDenial(buildRouteDenial(to, userRole))
          return home
        }

        // Nothing in this app admits this account, so every in-app redirect is
        // a bounce. End the session and land on login: while the session is
        // live the public branch above would send them straight back in, and
        // `denied` is what tells the login page to say why rather than
        // presenting a blank form to someone who just typed the right password.
        //
        // No denial is recorded here and any earlier one is dropped. This is
        // the portal's live path (a developer or legal account on
        // portal.claimshieldpro.com) and it is preserved byte for byte: same
        // logout, same `?denied=app`, same LoginView copy. Carrying the URL
        // across a logout would hand a signed-out browser a record of where a
        // signed-in one had tried to go, for a reader that does not exist.
        //
        // `reason: 'denied'`: identity sign-in also ends the sign-in server's
        // session for this account and comes back to the same no-access
        // screen, so "Use a different account" opens a clean email page.
        clearRouteDenial()
        await authStore.logout({ reason: 'denied' })
        return { name: 'login', query: { denied: 'app' } }
      }
    }

    return true
  })

  // Handle navigation errors
  router.onError((error, to) => {
    console.error('[Router] Navigation error:', error)

    // Chunk-load failures — a rebuild replaced every hashed chunk and this
    // document still references the old names. Recovery (engine-agnostic
    // matching + loop protection) lives in ./chunk-reload, the single
    // implementation shared with marketing and driver-portal.
    if (isChunkLoadError(error)) {
      console.log('[Router] Chunk load failed, reloading page...')
      attemptChunkReload(to.fullPath)
    }
  })

  // A resolved navigation means the chunk loaded, so the recovery is over —
  // drop the marker from the URL and reset the attempt budget. Done here
  // rather than inside chunk-reload.ts so that module stays free of a
  // vue-router import (driver-portal imports it without the shared barrel).
  router.afterEach((to) => {
    const cleaned = clearedChunkReloadQuery(to.query)
    if (cleaned) {
      void router.replace({ path: to.path, query: cleaned, hash: to.hash })
    }
  })
}
